import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Download, FileText, QrCode, Filter, ArrowUpDown, ChevronLeft } from 'lucide-react';
import { supabase } from '../services/db';
import { generateVoucherReportPDF, generateCollectiveCertificatePDF } from '../services/pdfService';
import QRCode from 'qrcode';

interface VoucherReportUser {
  user_id: string;
  user_email: string;
  user_name: string;
  company: string;
  registered_at: string;
  test_taken: boolean;
  test_score: number | null;
  test_passed: boolean | null;
  test_date: string | null;
  certificate_number: string | null;
  certificate_issued_at: string | null;
}

interface VoucherReportData {
  voucher: {
    id: string;
    code: string;
    partner: string;
    valid_from: string;
    valid_until: string;
    max_uses: number;
    remaining_uses: number;
  };
  statistics: {
    total_registrations: number;
    tests_taken: number;
    tests_passed: number;
    certificates_issued: number;
  };
  users: VoucherReportUser[];
  generated_at: string;
}

type SortField = 'user_name' | 'registered_at' | 'test_score' | 'certificate_issued_at';
type SortOrder = 'asc' | 'desc';
type TestFilter = 'all' | 'passed' | 'failed' | 'not_taken';
type CertificateFilter = 'all' | 'with_cert' | 'without_cert';

export default function AdminVoucherReportPage() {
  const { voucherId } = useParams<{ voucherId: string }>();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState<VoucherReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('user_name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [testFilter, setTestFilter] = useState<TestFilter>('all');
  const [certificateFilter, setCertificateFilter] = useState<CertificateFilter>('all');
  const [qrCodes, setQrCodes] = useState<Map<string, string>>(new Map());
  const [selectedQR, setSelectedQR] = useState<string | null>(null);

  useEffect(() => {
    if (voucherId) {
      fetchReportData();
    }
  }, [voucherId]);

  const fetchReportData = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('Nicht angemeldet');
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-voucher-report`;
      const response = await fetch(`${apiUrl}?voucherId=${voucherId}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error:', response.status, errorData);
        throw new Error(`Fehler beim Laden des Reports: ${errorData.error || response.statusText}`);
      }

      const data = await response.json();
      setReportData(data);

      const qrMap = new Map<string, string>();
      for (const user of data.users) {
        if (user.certificate_number) {
          try {
            const qrDataUrl = await QRCode.toDataURL(
              `https://ki-kompetenznachweis.online/verify/${user.certificate_number}`,
              { width: 100, margin: 1 }
            );
            qrMap.set(user.certificate_number, qrDataUrl);
          } catch (err) {
            console.error('Error generating QR code:', err);
          }
        }
      }
      setQrCodes(qrMap);

    } catch (err) {
      console.error('Error fetching report:', err);
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler');
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedUsers = useMemo(() => {
    if (!reportData) return [];

    let filtered = reportData.users.filter(user => {
      const matchesSearch =
        user.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.company.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesTestFilter =
        testFilter === 'all' ||
        (testFilter === 'passed' && user.test_passed === true) ||
        (testFilter === 'failed' && user.test_passed === false) ||
        (testFilter === 'not_taken' && !user.test_taken);

      const matchesCertFilter =
        certificateFilter === 'all' ||
        (certificateFilter === 'with_cert' && user.certificate_number) ||
        (certificateFilter === 'without_cert' && !user.certificate_number);

      return matchesSearch && matchesTestFilter && matchesCertFilter;
    });

    filtered.sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [reportData, searchTerm, sortField, sortOrder, testFilter, certificateFilter]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleExportPDF = async () => {
    if (!reportData) return;

    try {
      const pdf = await generateVoucherReportPDF(reportData);
      pdf.save(`Voucher-Report-${reportData.voucher.code}-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Fehler beim Erstellen des PDFs');
    }
  };

  const handleExportCollectivePDF = async () => {
    if (!reportData) return;

    try {
      const pdf = await generateCollectiveCertificatePDF(reportData.users);
      if (pdf) {
        pdf.save(`Sammel-Zertifikate-${reportData.voucher.code}-${new Date().toISOString().split('T')[0]}.pdf`);
      } else {
        alert('Keine Zertifikate zum Exportieren vorhanden');
      }
    } catch (err) {
      console.error('Error generating collective PDF:', err);
      alert('Fehler beim Erstellen des Sammel-PDFs');
    }
  };

  const handleExportCSV = () => {
    if (!reportData) return;

    const headers = ['Name', 'E-Mail', 'Unternehmen', 'Registriert am', 'Test absolviert', 'Test Score', 'Test bestanden', 'Zertifikatsnummer', 'Zertifikat ausgestellt am'];
    const rows = filteredAndSortedUsers.map(user => [
      user.user_name,
      user.user_email,
      user.company,
      new Date(user.registered_at).toLocaleDateString('de-DE'),
      user.test_taken ? 'Ja' : 'Nein',
      user.test_score !== null ? `${user.test_score}%` : '-',
      user.test_passed === true ? 'Ja' : user.test_passed === false ? 'Nein' : '-',
      user.certificate_number || '-',
      user.certificate_issued_at ? new Date(user.certificate_issued_at).toLocaleDateString('de-DE') : '-'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Voucher-Report-${reportData.voucher.code}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const resetFilters = () => {
    setSearchTerm('');
    setTestFilter('all');
    setCertificateFilter('all');
    setSortField('user_name');
    setSortOrder('asc');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Report wird geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Fehler</h2>
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={() => navigate('/admin/vouchers')}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
          >
            Zurück zur Voucher-Übersicht
          </button>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/vouchers')}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Zurück zur Voucher-Übersicht
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Voucher Report</h1>
              <p className="text-gray-600 mt-1">Detaillierte Übersicht aller Teilnehmer</p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleExportCSV}
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <Download className="w-5 h-5" />
                CSV Export
              </button>
              <button
                onClick={handleExportPDF}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                <FileText className="w-5 h-5" />
                PDF Report
              </button>
              <button
                onClick={handleExportCollectivePDF}
                className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700"
              >
                <FileText className="w-5 h-5" />
                Sammel-PDF
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Voucher-Details</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Voucher-Code</p>
              <p className="text-lg font-semibold text-gray-900">{reportData.voucher.code}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Partner</p>
              <p className="text-lg font-semibold text-gray-900">{reportData.voucher.partner}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Gültig von</p>
              <p className="text-lg font-semibold text-gray-900">
                {new Date(reportData.voucher.valid_from).toLocaleDateString('de-DE')}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Gültig bis</p>
              <p className="text-lg font-semibold text-gray-900">
                {new Date(reportData.voucher.valid_until).toLocaleDateString('de-DE')}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <p className="text-sm text-gray-600">Registrierungen</p>
            <p className="text-3xl font-bold text-blue-600">{reportData.statistics.total_registrations}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <p className="text-sm text-gray-600">Tests absolviert</p>
            <p className="text-3xl font-bold text-purple-600">{reportData.statistics.tests_taken}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <p className="text-sm text-gray-600">Tests bestanden</p>
            <p className="text-3xl font-bold text-green-600">{reportData.statistics.tests_passed}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <p className="text-sm text-gray-600">Zertifikate</p>
            <p className="text-3xl font-bold text-orange-600">{reportData.statistics.certificates_issued}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Filter & Suche</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <input
              type="text"
              placeholder="Suche nach Name, E-Mail oder Firma..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />

            <select
              value={testFilter}
              onChange={(e) => setTestFilter(e.target.value as TestFilter)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Alle Tests</option>
              <option value="passed">Test bestanden</option>
              <option value="failed">Test nicht bestanden</option>
              <option value="not_taken">Noch kein Test</option>
            </select>

            <select
              value={certificateFilter}
              onChange={(e) => setCertificateFilter(e.target.value as CertificateFilter)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Alle Zertifikate</option>
              <option value="with_cert">Mit Zertifikat</option>
              <option value="without_cert">Ohne Zertifikat</option>
            </select>

            <button
              onClick={resetFilters}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Filter zurücksetzen
            </button>
          </div>

          <p className="text-sm text-gray-600">
            {filteredAndSortedUsers.length} von {reportData.users.length} Teilnehmern angezeigt
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    onClick={() => handleSort('user_name')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-2">
                      Name
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    E-Mail
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unternehmen
                  </th>
                  <th
                    onClick={() => handleSort('registered_at')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-2">
                      Registriert
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('test_score')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-2">
                      Test
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Zertifikat
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    QR-Code
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAndSortedUsers.map((user) => (
                  <tr key={user.user_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.user_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {user.user_email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {user.company}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(user.registered_at).toLocaleDateString('de-DE')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.test_taken ? (
                        <div>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.test_passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {user.test_passed ? 'Bestanden' : 'Nicht bestanden'}
                          </span>
                          <p className="text-xs text-gray-600 mt-1">{user.test_score}%</p>
                        </div>
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                          Offen
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {user.certificate_number ? (
                        <div>
                          <p className="font-mono text-xs">{user.certificate_number}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(user.certificate_issued_at!).toLocaleDateString('de-DE')}
                          </p>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.certificate_number && qrCodes.has(user.certificate_number) ? (
                        <button
                          onClick={() => setSelectedQR(qrCodes.get(user.certificate_number!)!)}
                          className="p-2 hover:bg-gray-100 rounded"
                        >
                          <img
                            src={qrCodes.get(user.certificate_number!)!}
                            alt="QR Code"
                            className="w-12 h-12"
                          />
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedQR && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setSelectedQR(null)}
        >
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">QR-Code</h3>
              <button
                onClick={() => setSelectedQR(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <img src={selectedQR} alt="QR Code" className="w-full h-auto mb-4" />
            <a
              href={selectedQR}
              download="qr-code.png"
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              <Download className="w-5 h-5" />
              QR-Code herunterladen
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
